import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, FormControl, IconButton, InputLabel, List, ListItem, MenuItem, Paper, Select } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useQuery } from '@apollo/client';
import { getTechniciansForDepartmentsTemplate } from 'apollo/templates/user/query.graphql';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { User } from 'models/user';
import { Department } from 'models/organization';

type DepartmentTechnicianProps = {
    department?: Department;
    onTechniciansChange: (technicians: string[]) => void;
};
const DepartmentTechnicians: FC<DepartmentTechnicianProps> = ({ department, onTechniciansChange }) => {
    const { t: translate } = useTranslation(['lab-settings', 'workflow']);
    const [selectedTechnicians, setSelectedTechnicians] = useState<User[]>((department?.technicians as User[]) || []);
    const [addTechnicianId, setAddTechnicianId] = useState<string>('');
    const { showBoundary } = useDenthubErrorBoundary();

    const { data: { technicians } = {} } = useQuery(getTechniciansForDepartmentsTemplate, {
        variables: {
            filters: { active: true, role: 'technician' },
            limit: 0,
            offset: 0,
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const handleDeleteTechnician = (technicianId: string) => {
        setSelectedTechnicians((prev) => {
            const updatedTechnicians = prev.filter((technician) => technician._id !== technicianId);

            onTechniciansChange(updatedTechnicians.map((t) => t._id));
            return updatedTechnicians;
        });
    };

    const handleAddTechnician = (technicianId: string) => {
        if (!technicianId) return;

        const technicianToAdd = technicians.find((t: User) => t._id === technicianId);
        if (!technicianToAdd) return;

        setSelectedTechnicians((prev) => {
            if (prev.some((t) => t._id === technicianToAdd._id)) return prev;

            const updated = [...prev, technicianToAdd];
            onTechniciansChange(updated.map((t) => t._id));
            return updated;
        });

        setAddTechnicianId('');
    };

    return (
        <Box>
            <Paper variant="outlined">
                <List dense>
                    {selectedTechnicians.map((technician) => (
                        <ListItem
                            key={technician._id}
                            secondaryAction={
                                <IconButton onClick={() => handleDeleteTechnician(technician._id)} edge="end">
                                    <DeleteIcon />
                                </IconButton>
                            }
                            sx={{
                                mb: 1,
                            }}
                        >
                            <Box flexGrow={1}>{technician.fullName}</Box>
                        </ListItem>
                    ))}
                    <ListItem key={'add-technician-list-item'}>
                        <FormControl fullWidth size="small" sx={{ mt: 1 }}>
                            <InputLabel id="add-technician-label">
                                {translate('lab-settings:ADD_TECHNICIAN')}
                            </InputLabel>
                            <Select
                                labelId="add-technician-label"
                                value={addTechnicianId}
                                label={translate('lab-settings:ADD_TECHNICIAN')}
                                onChange={(e) => handleAddTechnician(e.target.value as string)}
                            >
                                {technicians
                                    ?.filter(
                                        (technician: User) =>
                                            !selectedTechnicians.some((t) => t._id === technician._id),
                                    )
                                    ?.map((technician: User) => (
                                        <MenuItem key={technician._id} value={technician._id}>
                                            {technician.fullName}
                                        </MenuItem>
                                    ))}
                            </Select>
                        </FormControl>
                    </ListItem>
                </List>
            </Paper>
        </Box>
    );
};

export default DepartmentTechnicians;
